import { FormikProvider, useField, useFormik } from 'formik';
import _ from 'lodash';
import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	useErrorBoundary,
	useLocalize,
	useLocalizedTranslation,
	useSupportedLanguageIDs,
	withLocalizedTranslation,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { ActionArchiveListItem } from '@treams/treams-ui/common/ActionListItem';
import { GraphQLErrorMessage } from '@treams/treams-ui/common/GraphQLErrorMessage';
import { Radio } from '@treams/treams-ui/common/Radio';
import { Select } from '@treams/treams-ui/common/Select';
import { Separator } from '@treams/treams-ui/common/Separator';
import { Spinner } from '@treams/treams-ui/common/Spinner';
import { Heading, Paragraph, Title } from '@treams/treams-ui/common/Text';
import { Button } from '@treams/treams-ui/common/button/Button';
import { Input as Input2 } from '@treams/treams-ui/form/controls/Input';

import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import useTalentReviewPath from '../hooks/useTalentReviewPath';
import { cellColorValue, isSupportedCellColor } from '../talentReviewColors';
import { placementHelpQuestionsAreReady } from '../talentReviewPlacementHelp';
import TalentReviewPlacementHelpEditor, {
	placementHelpQuestionsFromTemplate,
	placementHelpQuestionsInput,
} from './TalentReviewPlacementHelpEditor';
import TalentReviewGrid from './TalentReviewTemplateGrid';

function TalentReviewLanguageSelector() {
	const { t } = useTranslation();
	const [{ value: languageID }, , { setValue: setLanguageID }] = useField('languageID');
	const { languageIDs } = useSupportedLanguageIDs();

	const options = _.sortBy(
		languageIDs?.map((id) => ({
			label: t(`common.language.${id}`),
			value: id,
		})) ?? [],
		({ label }) => label,
	);

	return (
		<Box width={200}>
			<Select
				options={options.map(({ value, label }) => ({
					title: label,
					value,
				}))}
				selectedValue={languageID}
				onChange={(value) => setLanguageID(value!)}
				small
			/>
		</Box>
	);
}

const Warning = styled(Box)`
	background: ${(props) => rgba(props.theme.colors.yellow, 0.25)};
	border-radius: 4px;
`;

const talentReviewVisibilityKey: { [key in graphql.TalentReviewVisibility]: string } = {
	[graphql.TalentReviewVisibility.Manager]: 'manager',
	[graphql.TalentReviewVisibility.Members]: 'everyone',
	[graphql.TalentReviewVisibility.Self]: 'self',
};

const TalentReviewEditor = withLocalizedTranslation(function TalentReviewEditor(props: {
	template?: graphql.TalentReviewTemplate;
	groupID?: string;
	localizedT?: ReturnType<typeof useLocalizedTranslation>['localizedT'];
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const formik = useFormik({
		initialValues: {
			languageID: language.id,
			title: props.template?.title ?? [],
			description: props.template?.description ?? [],
			visibility: props.template?.visibility ?? graphql.TalentReviewVisibility.Self,
			outdatedAfterDays: props.template?.outdatedAfterDays ?? 30,
			hasNorm: props.template?.hasNorm ?? false,
			xAxis: {
				label:
					props.template?.xAxis.label ??
					props.localizedT!('talentReviews.v2.template.default.xAxis.label'),
				ticks: props.template?.xAxis.ticks ?? [
					props.localizedT!('talentReviews.v2.template.default.xAxis.ticks.0'),
					props.localizedT!('talentReviews.v2.template.default.xAxis.ticks.1'),
					props.localizedT!('talentReviews.v2.template.default.xAxis.ticks.2'),
				],
			},
			yAxis: {
				label:
					props.template?.yAxis.label ??
					props.localizedT!('talentReviews.v2.template.default.yAxis.label'),
				ticks: props.template?.yAxis.ticks ?? [
					props.localizedT!('talentReviews.v2.template.default.yAxis.ticks.0'),
					props.localizedT!('talentReviews.v2.template.default.yAxis.ticks.1'),
					props.localizedT!('talentReviews.v2.template.default.yAxis.ticks.2'),
				],
			},
			cells:
				props.template?.cells.map((row) =>
					row.map((cell) => ({
						..._.omit(cell, '__typename'),
						color: isSupportedCellColor(cell.color) ? cell.color : undefined,
					})),
				) ??
				Array.from({ length: 3 }, () =>
					Array.from({ length: 3 }, () => ({ label: [], instruction: [] })),
				),
			placementHelpQuestions: placementHelpQuestionsFromTemplate(props.template),
		},
		onSubmit: () => {},
	});

	const [create, { loading: createLoading }] = graphql.useCreateTalentReviewTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateTalentReviewTemplateMutation();

	const loading = createLoading || updateLoading;

	const history = useHistory();
	const path = useTalentReviewPath();

	const [error, attempt] = useErrorBoundary();

	const placementHelpReady = placementHelpQuestionsAreReady(
		formik.values.placementHelpQuestions,
		formik.values.languageID,
	);

	const onSave = attempt(async () => {
		if (!placementHelpReady) {
			return;
		}

		const input = {
			title: formik.values.title,
			description: formik.values.description,
			visibility: formik.values.visibility,
			outdatedAfterDays: formik.values.outdatedAfterDays,
			hasNorm: formik.values.hasNorm,
			xAxis: formik.values.xAxis,
			yAxis: formik.values.yAxis,
			cells: formik.values.cells.map((row, rowIndex) =>
				row.map((cell, columnIndex) => ({
					...cell,
					color: cellColorValue(
						rowIndex,
						columnIndex,
						formik.values.cells.length,
						row.length,
						'color' in cell ? cell.color : undefined,
					),
				})),
			),
			placementHelpQuestions: placementHelpQuestionsInput(formik.values.placementHelpQuestions),
		};

		if (props.template) {
			await update({ variables: { input: { ...input, templateID: props.template.id } } });
		} else {
			const response = await create({ variables: { input } });

			const templateID = response.data!.createTalentReviewTemplate.template.id;
			history.push(path({ groupID: props.groupID, templateID }));
		}
	});

	return (
		<FormikProvider value={formik}>
			<Flex mt={3} mb={4} flexDirection="row" alignItems="center">
				<Title>{t(`talentReviews.v2.template.${props.template ? 'edit' : 'create'}.title`)}</Title>
			</Flex>
			<Box mt={4} mb={2}>
				<Box mb={3}>
					<Heading>{t('talentReviews.v2.template.edit.details')}</Heading>
				</Box>
				<Separator />
			</Box>
			<GraphQLErrorMessage error={error} />
			<DetailsTableForm
				formValues={[
					{
						label: t('talentReviews.v2.template.edit.name'),
						name: 'title',
						type: DetailsTableFormValueTypes.Multilingual,
					},
					{
						label: t('talentReviews.v2.template.edit.description'),
						name: 'description',
						type: DetailsTableFormValueTypes.MultilingualQuill,
					},
				]}
			/>

			<Box mt={4} mb={2}>
				<Box mb={3}>
					<Heading>{t('talentReviews.v2.template.edit.visibility.label')}</Heading>
				</Box>
				<Separator />
			</Box>

			<Warning p={3} mb={2}>
				<Paragraph color="black">
					{t('talentReviews.v2.template.edit.visibility.warning')}
				</Paragraph>
			</Warning>

			{[
				graphql.TalentReviewVisibility.Members,
				graphql.TalentReviewVisibility.Self,
				graphql.TalentReviewVisibility.Manager,
			].map((visibility) => {
				const translationPrefix = `talentReviews.v2.template.edit.visibility.${
					talentReviewVisibilityKey[visibility]
				}`;

				return (
					<Radio.Option
						key={visibility}
						checked={formik.values.visibility === visibility}
						onChange={() => formik.setFieldValue('visibility', visibility)}
						title={t(`${translationPrefix}.title`)}
						description={t(`${translationPrefix}.description`)}
						maxWidth={600}
					/>
				);
			})}

			<Box mt={4} mb={2}>
				<Box mb={3}>
					<Heading>{t('talentReviews.v2.template.edit.outdatedAfterDays.label')}</Heading>
				</Box>
				<Separator />
			</Box>
			<Box maxWidth={600}>
				<Paragraph>{t('talentReviews.v2.template.edit.outdatedAfterDays.description')}</Paragraph>
			</Box>
			<Flex mt={2} alignItems="center" style={{ gap: 8 }}>
				<Box width={96}>
					<Input2
						type="number"
						value={String(formik.values.outdatedAfterDays)}
						onChange={(value) =>
							formik.setFieldValue('outdatedAfterDays', Math.max(1, parseInt(value) || 1))
						}
					/>
				</Box>
				<Paragraph>{t('talentReviews.v2.template.edit.outdatedAfterDays.unit')}</Paragraph>
			</Flex>

			<Box width="100%" pr={3}>
				<Box mt={4} mb={2}>
					<Flex mb={3} alignItems="center">
						<Box flexGrow={1} flexShrink={1}>
							<Heading>
								{formik.values.xAxis.ticks.length * formik.values.yAxis.ticks.length}{' '}
								{t('talentReviews.v2.template.edit.grid')}
							</Heading>
						</Box>
						<Box flexGrow={0} flexShrink={0}>
							<TalentReviewLanguageSelector />
						</Box>
					</Flex>
					<Separator />
				</Box>

				<TalentReviewGrid existing={!!props.template} />
			</Box>

			<TalentReviewPlacementHelpEditor />

			<Flex mt={2}>
				<Button
					title={t('talentReviews.v2.save')}
					onClick={() => onSave()}
					loading={loading}
					disabled={!placementHelpReady}
				/>
			</Flex>

			{props.template && (
				<Box mt={4}>
					<Box my={2}>
						<Heading>{t('talentReviews.v2.template.actions.title')}</Heading>
					</Box>

					<Box mb={3}>
						<Separator />
					</Box>

					<ActionArchiveListItem
						archivableItem={props.template}
						archivableItemName={t('talentReviews.v2.template.archivableItemName')}
						archivableItemKey="jobTitle"
						useArchive={graphql.useArchiveTalentReviewTemplateMutation}
						useRestore={graphql.useRestoreTalentReviewTemplateMutation}
					/>
				</Box>
			)}
		</FormikProvider>
	);
});

export default function TalentReviewEditorWithID(props: { id?: string; groupID?: string }) {
	const query = graphql.useTalentReviewTemplateQuery({
		variables: { id: props.id! },
		skip: !props.id,
	});

	if (!props.id) {
		return <TalentReviewEditor groupID={props.groupID} />;
	} else if (query.data) {
		return (
			<TalentReviewEditor
				template={query.data.talentReviewTemplate as graphql.TalentReviewTemplate}
				groupID={props.groupID}
			/>
		);
	} else {
		return <Spinner centered />;
	}
}
